import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Gamepad2, Monitor, Smartphone, LogOut, ShieldCheck, ExternalLink } from "lucide-react";

const navItems = [
  { to: "/", label: "Início", testid: "nav-home" },
  { to: "/mobile", label: "Mobile", testid: "nav-mobile", icon: Smartphone },
  { to: "/pc", label: "PC", testid: "nav-pc", icon: Monitor },
  { to: "/consola", label: "Consola", testid: "nav-consola", icon: Gamepad2 },
];

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link
          to="/"
          data-testid="brand-link"
          className="flex items-center gap-3 group"
        >
          <div className="relative grid h-9 w-9 place-items-center bg-[#00F0FF] text-black">
            <Trophy className="h-5 w-5" strokeWidth={2.5} />
            <span className="absolute -bottom-1 -right-1 h-2 w-2 bg-[#FF3B30] pulse-live" />
          </div>
          <div className="leading-none">
            <div className="font-display text-lg tracking-wider uppercase">
              ESTEL
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#00F0FF]">
              Torneio 25/26
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              end={n.to === "/"}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium uppercase tracking-wider transition-colors ${
                  isActive
                    ? "text-[#00F0FF]"
                    : "text-white/70 hover:text-white"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="https://estel.edu.pt/"
            target="_blank"
            rel="noreferrer"
            data-testid="header-estel-link"
            className="hidden sm:inline-flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-white/80 hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"
            title="Site oficial ESTEL"
          >
            estel.edu.pt
            <ExternalLink className="h-3 w-3" />
          </a>
          {user && user.role === "admin" ? (
            <>
              <button
                data-testid="admin-dashboard-btn"
                onClick={() => navigate("/admin")}
                className="hidden sm:inline-flex items-center gap-2 border border-[#00F0FF]/60 px-3 py-1.5 text-xs uppercase tracking-wider text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black transition-colors"
              >
                <ShieldCheck className="h-3.5 w-3.5" />
                Admin
              </button>
              <button
                data-testid="logout-btn"
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                className="grid h-9 w-9 place-items-center border border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
                title="Sair"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <Link
              to="/admin/login"
              data-testid="header-admin-login"
              className="text-xs uppercase tracking-[0.2em] text-white/40 hover:text-[#00F0FF] transition-colors"
            >
              · Admin
            </Link>
          )}
        </div>
      </div>

      {/* Mobile nav */}
      <nav className="md:hidden flex items-center justify-around border-t border-white/5 bg-black/40">
        {navItems.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`m-${n.testid}`}
            end={n.to === "/"}
            className={({ isActive }) =>
              `flex-1 py-2 text-center text-[11px] uppercase tracking-wider transition-colors ${
                isActive ? "text-[#00F0FF]" : "text-white/60"
              }`
            }
          >
            {n.label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
}
